import { createRouter, createWebHistory } from 'vue-router'

import { ensureSession, startLogin } from '@/libs/auth'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:     '/',
            redirect: '/mail',
        },
        // Main is where mail opens (func spec §5.2 — it is the pinned entry
        // above the roots and the place the owner looks first), so the bare
        // path is a redirect to it rather than a view of its own.
        {
            path:     '/mail',
            redirect: '/mail/main',
        },
        // Main and its three unified views (func spec §5.2, §5.2.5). Like the
        // outbox below they span every root, so they have their own paths above
        // the root/folder pair. The static ones are declared first, so
        // /mail/main/sent can never be read as a conversation id.
        {
            path:      '/mail/main',
            name:      'mail-main',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/main/sent',
            name:      'mail-main-sent',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/main/drafts',
            name:      'mail-main-drafts',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/main/flagged',
            name:      'mail-main-flagged',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/main/:conversationId',
            name:      'mail-main-conversation',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        // The outbox is a view of the mailbox rather than a folder (func spec
        // §11.8): it spans every root and holds nothing that has been filed
        // anywhere yet, so it has its own path above the root/folder pair.
        {
            path:      '/mail/outbox',
            name:      'mail-outbox',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        // A saved search is a view of the mailbox in the same sense the outbox is
        // (func spec §21.5 — pinned to the sidebar, "behaves as a live folder"):
        // its rows come from wherever the query found them, so it has its own path
        // above the root/folder pair — and BEFORE it, since both are three
        // segments and this one is the specific reading of them.
        {
            path:      '/mail/saved/:savedId',
            name:      'mail-saved',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/:rootId/:folderId',
            name:      'mail-folder',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        {
            path:      '/mail/:rootId/:folderId/:conversationId',
            name:      'mail-conversation',
            component: () => import('@/views/mail/Mailbox.vue'),
            meta:      { requiresAuth: true, layout: 'Mail' },
        },
        // Calendar is the second TOP-LEVEL MODE (func spec §23.1), with its own
        // shell and its own state. The bare path carries no view and no date
        // because neither is a fact this file knows: §25's default view is "Week
        // (desktop) / Agenda (mobile)", which only the running app can resolve,
        // and "today" is a fact about the owner's clock. The view resolves both
        // and replaces itself with the URL it settled on, so what is in the
        // address bar is always the view actually on screen.
        {
            path:      '/calendar',
            name:      'calendar',
            component: () => import('@/views/calendar/Calendar.vue'),
            meta:      { requiresAuth: true, layout: 'Calendar' },
        },
        {
            path:      '/calendar/:view(week|day|agenda|month)/:date?',
            name:      'calendar-view',
            component: () => import('@/views/calendar/Calendar.vue'),
            meta:      { requiresAuth: true, layout: 'Calendar' },
        },
        // Settings is a shell with a left nav column and one page per section
        // (views/settings/Settings.vue): the nav and the column width belong to
        // the section, not to any one page, so they are rendered once by the
        // parent and the pages are children of it.
        //
        // Owner feedback (2026-08-19): the old three-page split — Connections,
        // "Identities and signatures", "Appearance, reading and sending" — read
        // as furniture rather than an information architecture, and buried
        // signature editing in a cramped dialog. Eight single-word sections
        // replace it, Accounts first because it is the one the owner opens most
        // (adding, fixing, or checking on a connection). Every URL below is
        // named for what is IN it, not for the settings table's old grouping.
        {
            path:      '/settings',
            component: () => import('@/views/settings/Settings.vue'),
            redirect:  { name: 'settings-accounts' },
            meta:      { requiresAuth: true, layout: 'Settings' },
            children:  [
                // Accounts is a master-detail pair (owner decision 2026-08-30):
                // a flat list of connections, and everything about one of them
                // on its own page. The collapsible card that held both nested
                // four levels deep and was unreadable. An id that names no
                // connection redirects back to the list from the detail page
                // itself — only the server can say whether it exists.
                {
                    path:      'accounts',
                    name:      'settings-accounts',
                    component: () => import('@/views/settings/SettingsAccounts.vue'),
                },
                {
                    path:      'accounts/:connectionId',
                    name:      'settings-account',
                    component: () => import('@/views/settings/SettingsAccount.vue'),
                },
                // The composition editor, the default identity and the root
                // markers — everything that shapes what Main shows and sends as
                // (func spec §5.2, §11.2, §5.3).
                {
                    path:      'main',
                    name:      'settings-main',
                    component: () => import('@/views/settings/SettingsMain.vue'),
                },
                // The signature library, authored here and assigned on an
                // account's identities (func spec §11.5). Its own section
                // because editing one under a list of all of them said nothing
                // about which one was open (owner feedback, round 6).
                {
                    path:      'signatures',
                    name:      'settings-signatures',
                    component: () => import('@/views/settings/SettingsSignatures.vue'),
                },
                {
                    path:      'reading',
                    name:      'settings-reading',
                    component: () => import('@/views/settings/SettingsReading.vue'),
                },
                // The §25 Notifications group: the per-root and per-folder rules
                // (§22.1 Layer 2), the deterministic filters (Layer 3), what an
                // alert may show (§22.2), quiet hours (§22.4) and the event
                // reminder switch (§22.3).
                {
                    path:      'notifications',
                    name:      'settings-notifications',
                    component: () => import('@/views/settings/SettingsNotifications.vue'),
                },
                // The calendar's own knobs (func spec §25's Calendar group).
                {
                    path:      'calendar',
                    name:      'settings-calendar',
                    component: () => import('@/views/settings/SettingsCalendar.vue'),
                },
                {
                    path:      'sending',
                    name:      'settings-sending',
                    component: () => import('@/views/settings/SettingsSending.vue'),
                },
                {
                    path:      'appearance',
                    name:      'settings-appearance',
                    component: () => import('@/views/settings/SettingsAppearance.vue'),
                },
                {
                    path:      'shortcuts',
                    name:      'settings-shortcuts',
                    component: () => import('@/views/settings/SettingsShortcuts.vue'),
                },
                // The search-term reference (func spec §21.3). Its own section
                // rather than a card under Shortcuts: that page is the keyboard
                // and nothing else, and a URL here is named for what is in it.
                {
                    path:      'search',
                    name:      'settings-search',
                    component: () => import('@/views/settings/SettingsSearch.vue'),
                },
                {
                    path:      'archive',
                    name:      'settings-archive',
                    component: () => import('@/views/settings/SettingsArchive.vue'),
                },
                // The owner's password (tech spec §8.4). Beside Passkeys because
                // the two are the same credential presented two ways, and its own
                // section for the reason every URL here is named for what is in
                // it — "Accounts" in this app means a mail account, so a password
                // block under one of those would be about the wrong credential.
                {
                    path:      'password',
                    name:      'settings-password',
                    component: () => import('@/views/settings/SettingsPassword.vue'),
                },
                // The owner's passkeys (tech spec §8.4). Its own section rather than
                // a card under another one, for the reason every URL here is named
                // for what is in it: this is where the owner goes to add a device or
                // to take a lost one away.
                {
                    path:      'passkeys',
                    name:      'settings-passkeys',
                    component: () => import('@/views/settings/SettingsPasskeys.vue'),
                },
                // The old three URLs, kept so a bookmark or a pasted link never
                // 404s. connections and mail both landed identities and
                // signatures on one page or another of the old split; accounts
                // is where both live now. app was three unrelated cards at
                // once — reading is the first of the three it becomes.
                { path: 'connections', redirect: { name: 'settings-accounts' } },
                { path: 'mail',        redirect: { name: 'settings-accounts' } },
                { path: 'app',         redirect: { name: 'settings-reading' } },
            ],
        },
        {
            path:      '/auth/login',
            name:      'login',
            component: () => import('@/views/auth/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        // Any unknown path lands in the mailbox rather than a blank page.
        { path: '/:pathMatch(.*)*', redirect: '/mail' },
    ],
})

// Protected unless the route says `requiresAuth: false` — a route that forgets its meta must
// fail closed, into the login flow, not open (vue-spec bootstrap.md step 17). Only the login
// page opts out. The routes here without meta are all redirects, which vue-router resolves
// before any guard runs, so the guard only ever sees a route that renders something.
router.beforeEach(async (to, _from, next) => {
    if (to.meta.requiresAuth === false) return next()

    // BFF model: no local credential state — a live server session is the truth.
    const authenticated = await ensureSession()
    if (!authenticated) {
        startLogin()
        return next(false)
    }

    next()
})

export default router
